import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Auth, Amplify, graphqlOperation, API } from "aws-amplify";
import {
  useToast,
  Button,
  Center,
  CircularProgress,
  Flex,
  Box,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { getGuest, getUsers } from "../../graphql/queries";
import { signInUser } from "../../Features/userSlice";
import { findBestMatch } from "../../Helper/searchEngine";
import { BookingTile } from "../Component/bookingTile";
import { UserReservationDrawer } from "../Mini/userReservationDrawer";
import axios from 'axios';


Amplify.configure({
  API: {
    graphql_headers: async () => {
      const session = await Auth.currentSession();
      return {
        Authorization: session.getIdToken().getJwtToken(),
      };
    },
  },
});

export const SignInHandle = () => {
  const dispatch = useDispatch();
  let signInInProgress = false;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { currentUser, status, error } = useSelector((state) => state.user);
  const [guest, setGuest] = useState(null);
  const [roomBookings, setRoomBookings] = useState(null);

  const [loading, setLoading] = useState(true);
  const [showFindBooking, setShowFindBooking] = useState(false);
  const toast = useToast();

  const storedId = useSelector((state) => state.user.id);
  const storedEmail = useSelector((state) => state.user.email);
  const storedPassword = useSelector((state) => state.user.password);
  const storedStripeId = useSelector((state) => state.user.stripeId);
  const propertyId = useSelector((state) => state.room.propertyId);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);

  const guestName = useSelector((state) => state.guest.guestName);
  const bookingId = useSelector((state) => state.guest.bookingId);
  const bID = useSelector((state) => state.guest.bID);

  // useEffect(() => {
  //   if (storedId && storedEmail && storedPassword) {
  //     handleFindBestMatch();
  //   } else {
  //     console.log("No persisted data found.");
  //     setLoading(false);
  //     toast({
  //       title: "Please use the primary link",
  //       description: "Please uset the link provided to you in the messages",
  //       status: "error",
  //       duration: 9000,
  //       isClosable: true,
  //     });
  //   }
  // }, [storedId, storedEmail, storedPassword]);

  // 2) Try sign-in or show toast if no credentials
  useEffect(() => {
    const init = async () => {
      if (storedId && storedEmail && storedPassword) {
        await handleFindBestMatch(); // attempt sign-in / best match
        // Only fetch room bookings after handleFindBestMatch completes
        
        if (propertyId !== null && propertyId !== undefined) {
       
          try {
            const response = await axios.get(`https://api.linkbase.ca/bookings/by-room/${propertyId}`, {
              headers: {
                'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY
              }
            });
            
            // Get the results array from the response
            const bookingsData = response?.data?.results;
  
            
            if (Array.isArray(bookingsData)) {
              // Store the results array directly
              dispatch({ type: 'room/setRoomBookings', payload: bookingsData });
            } else {
              console.error('Unexpected data structure:', response.data);
            }
          } catch (error) {
            console.error('Error fetching room bookings:', error);
            toast({
              title: "Error fetching bookings",
              description: "There was an issue fetching the room bookings.",
              status: "error",
              duration: 9000,
              isClosable: true,
            });
          }
        } else {
          console.log('No propertyId available, skipping bookings fetch');
        }
      } else {
        console.log("No persisted data found.");
        toast({
          title: "Please use the primary link",
          description: "Use the link provided to you in the messages",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    };

    init();
  }, [storedId, storedEmail, storedPassword, propertyId, dispatch]);

  // Add this useEffect for handling property bookings fetch
  useEffect(() => {
    const fetchPropertyBookings = async () => {
      if (propertyId) {
        try {
          const response = await axios.get(`https://api.linkbase.ca/bookings/by-room/${propertyId}`, {
            headers: {
              'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY
            }
          });
          
          const bookingsData = response?.data?.results;
  
          
          if (Array.isArray(bookingsData)) {
            dispatch({ type: 'room/setRoomBookings', payload: bookingsData });

            // Check if we have saved dates and try to find a matching booking
            const savedCheckIn = localStorage.getItem('propertyCheckInDate');
            const savedCheckOut = localStorage.getItem('propertyCheckOutDate');
            
            if (savedCheckIn && savedCheckOut) {
              const searchCheckIn = new Date(savedCheckIn).toISOString().split('T')[0];
              const searchCheckOut = new Date(savedCheckOut).toISOString().split('T')[0];
              
              const matchingBooking = bookingsData.find(booking => {
                const bookingItem = booking.Bookings?.items?.[0];
                return bookingItem?.checkInDate === searchCheckIn && 
                       bookingItem?.checkOutDate === searchCheckOut;
              });

              if (matchingBooking) {
                setGuest(matchingBooking);
                // Close drawer if it's open and we found a match
                if (isOpen) {
                  onClose();
                }
                setShowFindBooking(false);
              } else {
                setShowFindBooking(true);
                onOpen();
              }
            }
          }
        } catch (error) {
          console.error('Error fetching room bookings:', error);
          toast({
            title: "Error fetching bookings",
            description: "There was an issue fetching the room bookings.",
            status: "error",
            duration: 9000,
            isClosable: true,
          });
        }
      }
    };

    fetchPropertyBookings();
  }, [propertyId, dispatch, toast, onClose, onOpen, isOpen]);

  const checkUserSession = async () => {
    try {
      // Check if a user is already authenticated
      const user = await Auth.currentAuthenticatedUser();
      console.log("User already signed in:", user);

      // If the stored ID is different from the current user ID, sign them out
      if (storedId !== user.attributes.sub) {
        console.log("User ID has changed. Signing out current user...");
        await signOut();
        await signIn(storedEmail, storedPassword);
      } else {
        dispatch(signInUser()); // User ID matches, keep them signed in
        return user;
      }
    } catch (error) {
      if (error === "The user is not authenticated") {
        // Handle the case where the user is not authenticated
        console.log("The user is not authenticated. Attempting to sign in...");
        signIn(storedEmail, storedPassword);
      } else {
        console.error("Error during sign-in check:", error);
        toast({
          title: "There was an issue",
          description: error,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    }
  };

  const signIn = async (email, password) => {
    if (signInInProgress) {
      console.log("Sign-in already in progress, skipping this attempt.");
      return;
    }

    signInInProgress = true; // Set the lock

    try {
      // Check if a user is currently signed in
      const user = await Auth.currentAuthenticatedUser();
      if (user) {
        // If a user is signed in and their ID matches the stored ID, do nothing
        if (storedId === user.attributes.sub) {
          console.log("User ID matches, no need to sign out.");
          signInInProgress = false; // Release the lock
          return;
        } else {
          // If the user ID does not match, sign them out
          console.log("Signing out current user...");
          await signOut();
        }
      }
    } catch (error) {
      if (error !== "The user is not authenticated") {
        console.error("Error checking current user:", error);
      }
    }

    // Now attempt to sign in the new user
    try {
      const user = await Auth.signIn(email, password);
      dispatch(signInUser());
      handleFindBestMatch();
    } catch (err) {
      if (err.code === "NotAuthorizedException") {
        toast({
          title: "Authentication Failed",
          description: "Incorrect username or password.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } else if (err.code === "UserNotFoundException") {
        toast({
          title: "User Not Found",
          description: "The username does not exist.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } else {
        if (err.message !== "Pending sign-in attempt already in progress") {
          toast({
            title: "There was an issue",
            description: err.message,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
        }
        console.error("Error during sign-in:", err);
      }
    } finally {
      signInInProgress = false; // Always release the lock
    }
  };

  const signOut = async () => {
    try {
      await Auth.signOut();
      dispatch(signInUser(null)); // Clear the user from the state
      console.log("Signed out successfully");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const formatDateToYyyyMmDd = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleFindBestMatch = async () => {
    const user = await checkUserSession();

    if (!user) {
      return;
    }

    const searchCriteria = {
      firstName: guestName ? guestName.split(" ")[0] : firstName || "",
      lastName: guestName ? guestName.split(" ")[1] : lastName || "",
      reservationID: bookingId || "",
      bID: bID || "",
      checkInDate: selectedDate ? formatDateToYyyyMmDd(selectedDate) : "",
      ownerID: user ? `${user.username}::${user.username}` : "",
    };

    try {
      const bestMatch = await findBestMatch(searchCriteria);

      if (bestMatch) {
        if (bestMatch.isSecure) {
          setGuest(bestMatch);
        } else {
          toast({
            title: "Wrong information provided",
            description:
              "Please be sure to input the correct info for your booking.",
            status: "info",
            duration: 5000,
            isClosable: true,
          });
          setShowFindBooking(true);
          // Only open drawer if we don't have a property match
          if (!guest) {
            onOpen();
          }
        }
      } else {
        console.error("No match found.");
        setShowFindBooking(true);
        // Only open drawer if we don't have a property match
        if (!guest) {
          onOpen();
        }
      }
    } catch (error) {
      console.error("Error finding best match:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setShowFindBooking(false);
    handleFindBestMatch();
    onClose();
  };

  return (
    <>
      <Flex
        px={4}
        justifyContent={"flex-start"}
        flexDirection="column"
        mt={"70px"}
      >
        <Center flexDirection="column" alignItems={"start"}>
          <Text fontWeight={800} pb={2} fontSize={"12px"}>
            Your Bookings
          </Text>
        </Center>

        {guest?.Bookings?.items?.length > 0 ? (
          <Flex mt={2} flexDirection="column">
            {guest.Bookings.items.map((booking, index) => (
              <Box key={index}>
                <BookingTile
                  booking={booking}
                  storedStripeId={storedStripeId}
                />
              </Box>
            ))}
          </Flex>
        ) : showFindBooking ? (
          <Center h="50vh" flexDirection="column">
            <Text fontWeight={800} pt={4} fontSize="12px">
              No bookings found.
            </Text>
            <Button
              onClick={onOpen}
              mt={4}
              w={"80%"}
              bg={"#817efc"}
              variant="solid"
              borderRadius="10px"
              fontSize="14px"
            >
              Find Booking
            </Button>
          </Center>
        ) : (
          <Center h="50vh" flexDirection="column">
            <CircularProgress
              isIndeterminate
              color="#504CFF"
              size="40px"
              trackColor="transparent"
            />
            <Text fontWeight={800} pt={4} fontSize="12px">
              Looking for your booking...
            </Text>
          </Center>
        )}
      </Flex>

      <UserReservationDrawer
        isOpen={isOpen}
        onClose={onClose}
        firstName={firstName}
        setFirstName={setFirstName}
        lastName={lastName}
        setLastName={setLastName}
        setSelectedDate={setSelectedDate}
        selectedDate={selectedDate}
        handleSearch={handleSearch}
        setGuest={setGuest}
      />
    </>
  );
};
